/**
 * Classifies an API/network error into a translation key for user display.
 *
 * Returns codes, not copy: the keys resolve against the
 * shared.errors.translations.errors puff bundle, and FormError (or any
 * other component) does the actual t(key) lookup. This keeps English out
 * of the non-component layer per the coding standards.
 */

/** Message keys in the shared.errors.translations.errors bundle. */
export const UserMessageKey = {
  Unexpected: 'errorUnexpected',
  Fallback: 'errorFallback',
  Validation: 'errorValidation',
  Network: 'errorNetwork',
  NotFound: 'errorNotFound',
  Permission: 'errorPermission',
  Conflict: 'errorConflict',
  Throttled: 'errorThrottled',
  Unavailable: 'errorUnavailable',
} as const;
export type UserMessageKey = (typeof UserMessageKey)[keyof typeof UserMessageKey];

export interface ResolvedUserError {
  /** Translation key for the user-facing message. */
  readonly key: UserMessageKey;
  /**
   * Service-supplied message (e.g. "Cannot change due date for OVERDUE
   * tasks"). When present, it is more specific than the generic translated
   * copy and should be shown instead. Note: service messages are not
   * localized — the translated key copy is the fallback, not a replacement.
   */
  readonly serviceMessage?: string | undefined;
}

/** Known typed-client exception names → message keys. */
/**
 * ValidationException gets special treatment: its message is surfaced even
 * for hand-rolled errors without Smithy's `$fault` marker, since callers
 * (e.g. legacy REST paths) construct it directly.
 */
const VALIDATION_EXCEPTION = 'ValidationException';

const KEY_BY_ERROR_NAME = new Map<string, UserMessageKey>([
  [VALIDATION_EXCEPTION, UserMessageKey.Validation],
  ['ResourceNotFoundException', UserMessageKey.NotFound],
  ['NotFoundException', UserMessageKey.NotFound],
  ['AccessDeniedException', UserMessageKey.Permission],
  ['UnauthorizedException', UserMessageKey.Permission],
  ['ConflictException', UserMessageKey.Conflict],
  ['VersionConflictException', UserMessageKey.Conflict],
  ['ThrottlingException', UserMessageKey.Throttled],
  ['TooManyRequestsException', UserMessageKey.Throttled],
  ['ServiceUnavailableException', UserMessageKey.Unavailable],
  ['InternalServerError', UserMessageKey.Unavailable],
]);

const isNetworkError = (error: Error): boolean =>
  error.message.includes('Network Error') || error.message.includes('ECONNREFUSED');

/**
 * Smithy service exceptions carry `$fault` ("client" | "server"), set only
 * when the error was deserialized from an actual service response. Only
 * client-fault (4xx) messages are surfaced: they are written for consumers
 * (validation failures, state conflicts, ...). Server-fault (5xx) messages
 * are often technical internals, so those keep the generic translated copy.
 */
const isClientServiceException = (error: Error): boolean =>
  '$fault' in error && (error as { $fault?: unknown }).$fault === 'client';

export function resolveUserError(error: unknown): ResolvedUserError {
  if (!error) return { key: UserMessageKey.Unexpected };
  if (!(error instanceof Error)) return { key: UserMessageKey.Fallback };

  const serviceMessage =
    (isClientServiceException(error) || error.name === VALIDATION_EXCEPTION) &&
    error.message
      ? { serviceMessage: error.message }
      : {};

  const known = KEY_BY_ERROR_NAME.get(error.name);
  if (known) return { key: known, ...serviceMessage };

  if (isNetworkError(error)) return { key: UserMessageKey.Network };

  return { key: UserMessageKey.Fallback, ...serviceMessage };
}
